import { useState, useEffect, useCallback, useRef } from 'react';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { useEditor, EditorContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import { Node, mergeAttributes } from '@tiptap/core';

// Custom extension for template variables
const TemplateVariable = Node.create({
  name: 'templateVariable',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      variable: { default: '' },
      label: { default: '' },
      category: { default: '' },
      display: { default: 'inline' },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-template-variable]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, {
      'data-template-variable': HTMLAttributes.variable,
      'class': 'template-variable',
    }), `{{${HTMLAttributes.variable}}}`];
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateVariableView);
  },
});

// React component for rendering template variables in the editor
function TemplateVariableView({ node }: NodeViewProps) {
  return (
    <NodeViewWrapper as="span" className="inline">
      <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-primary/10 text-primary text-sm font-medium border border-primary/20">
        {node.attrs.label || node.attrs.variable}
      </span>
    </NodeViewWrapper>
  );
}
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  Plus,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Building2,
  User,
  Receipt,
  FileQuestion,
  CreditCard,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  PenLine,
  Trash2,
  Save,
  Loader2,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useApp } from '@/components/AppLayout';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { contractTemplateApi } from '@/lib/api';
import type { ContractTemplate, ContractTemplateDocumentType, ContractVariableCategory, ContractVariable } from '@/lib/api';

// Icon mapping for variable categories (API returns data, icons are client-side)
const CATEGORY_ICONS: Record<string, typeof Building2> = {
  business: Building2,
  client: User,
  invoice: Receipt,
  quote: FileQuestion,
};

// Editor toolbar component
function EditorToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-1 p-2 border-b bg-muted/30">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('bold') && 'bg-muted')}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('italic') && 'bg-muted')}
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('underline') && 'bg-muted')}
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-6 mx-1" />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={cn('h-8 w-8 p-0', editor.isActive('heading', { level: 1 }) && 'bg-muted')}
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={cn('h-8 w-8 p-0', editor.isActive('heading', { level: 2 }) && 'bg-muted')}
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Separator orientation="vertical" className="h-6 mx-1" />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('bulletList') && 'bg-muted')}
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn('h-8 w-8 p-0', editor.isActive('orderedList') && 'bg-muted')}
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
    </div>
  );
}

// Variable category collapsible component
function VariableCategorySection({
  category,
  onInsert,
}: {
  category: ContractVariableCategory;
  onInsert: (variable: ContractVariable, categoryId: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const Icon = CATEGORY_ICONS[category.id] || Building2;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button className="flex items-center justify-between w-full px-3 py-2 text-sm hover:bg-muted rounded-lg transition-colors">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{category.name}</span>
          </div>
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-6 pr-2 py-1 space-y-1">
          {category.variables.map((variable) => (
            <button
              key={variable.id}
              onClick={() => onInsert(variable, category.id)}
              className="w-full px-2 py-1.5 text-xs text-left hover:bg-primary/10 hover:text-primary rounded transition-colors"
            >
              {variable.label}
            </button>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

type DocumentType = ContractTemplateDocumentType;

// Signature block component
function SignatureBlock({
  showBusiness,
  showCounterparty,
  businessName,
  documentType,
}: {
  showBusiness: boolean;
  showCounterparty: boolean;
  businessName: string;
  documentType: DocumentType;
}) {
  if (!showBusiness && !showCounterparty) return null;

  const isVendor = documentType === 'purchase_order';
  const counterpartyLabel = isVendor ? 'Vendor' : 'Client';

  return (
    <div className="border-t-2 border-dashed border-muted-foreground/20 mt-8 pt-8 px-8 pb-8">
      <div className={cn(
        "grid gap-12",
        showBusiness && showCounterparty ? "grid-cols-2" : "grid-cols-1 max-w-xs"
      )}>
        {showBusiness && (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="h-16 border-b-2 border-foreground/80 flex items-end pb-1">
                <PenLine className="h-4 w-4 text-muted-foreground/40" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">{businessName}</p>
                <p className="text-xs text-muted-foreground">Service/Goods Provider</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Date:</span>
              <div className="flex-1 border-b border-muted-foreground/40" />
            </div>
          </div>
        )}
        {showCounterparty && (
          <div className="space-y-4">
            <div className="space-y-3">
              <div className="h-16 border-b-2 border-foreground/80 flex items-end pb-1">
                <PenLine className="h-4 w-4 text-muted-foreground/40" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">{counterpartyLabel} Name</p>
                <p className="text-xs text-muted-foreground">{counterpartyLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Date:</span>
              <div className="flex-1 border-b border-muted-foreground/40" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ContractEditor() {
  const { nav } = useBusinessNav();
  const { currentBusiness } = useApp();

  // API-loaded templates
  const [templates, setTemplates] = useState<ContractTemplate[]>([]);
  const [variableCategories, setVariableCategories] = useState<ContractVariableCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);

  // UI state
  const [contractName, setContractName] = useState('');
  const [showBusinessSignature, setShowBusinessSignature] = useState(false);
  const [showCounterpartySignature, setShowCounterpartySignature] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [pendingNavigationPath, setPendingNavigationPath] = useState<string | null>(null);
  const [pendingTemplateId, setPendingTemplateId] = useState<string | null>(null);
  const [newContractName, setNewContractName] = useState('');
  const [newContractDocType, setNewContractDocType] = useState<DocumentType>('invoice');

  // Track dirty state
  const [isDirty, setIsDirty] = useState(false);
  const [initialContent, setInitialContent] = useState<string>('');
  const [initialName, setInitialName] = useState<string>('');
  const [initialBusinessSig, setInitialBusinessSig] = useState(false);
  const [initialCounterpartySig, setInitialCounterpartySig] = useState(true);
  const isLoadingContent = useRef(false);

  const currentTemplate = templates.find(t => t.id === selectedTemplateId);
  const enabledDocType: DocumentType = currentTemplate?.document_type || 'invoice';

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TemplateVariable,
      Placeholder.configure({
        placeholder: 'Start writing your contract...',
      }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none focus:outline-none min-h-[500px] px-8 py-6',
      },
    },
    onUpdate: () => {
      // Don't mark dirty during initial content load
      if (!isLoadingContent.current) {
        setIsDirty(true);
      }
    },
  });

  // Check if there are unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!editor || !currentTemplate) return false;
    const currentContent = JSON.stringify(editor.getJSON());
    return (
      isDirty &&
      (currentContent !== initialContent ||
        contractName !== initialName ||
        showBusinessSignature !== initialBusinessSig ||
        showCounterpartySignature !== initialCounterpartySig)
    );
  }, [editor, currentTemplate, isDirty, initialContent, initialName, initialBusinessSig, initialCounterpartySig, contractName, showBusinessSignature, showCounterpartySignature]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load templates and variables from API
  useEffect(() => {
    async function loadData() {
      if (!currentBusiness) return;
      setLoading(true);

      // Load templates and variables in parallel
      const [templatesResponse, variablesResponse] = await Promise.all([
        contractTemplateApi.list(currentBusiness.id),
        contractTemplateApi.getVariables(),
      ]);

      if (templatesResponse.data) {
        setTemplates(templatesResponse.data);
        // Select first template if none selected
        if (templatesResponse.data.length > 0 && !selectedTemplateId) {
          setSelectedTemplateId(templatesResponse.data[0].id);
        }
      }

      if (variablesResponse.data) {
        setVariableCategories(variablesResponse.data);
      }

      setLoading(false);
    }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only re-run when business changes, not when selectedTemplateId changes
  }, [currentBusiness]);

  // Update editor when template changes
  useEffect(() => {
    if (currentTemplate && editor) {
      // Prevent onUpdate from marking dirty during load
      isLoadingContent.current = true;

      // Try to parse as JSON (Tiptap format), fallback to HTML
      try {
        const content = JSON.parse(currentTemplate.content);
        editor.commands.setContent(content, { emitUpdate: false });
      } catch {
        editor.commands.setContent(currentTemplate.content, { emitUpdate: false });
      }

      // Update UI state
      setContractName(currentTemplate.name);
      setShowBusinessSignature(currentTemplate.business_signature_required);
      setShowCounterpartySignature(currentTemplate.counterparty_signature_required);

      // Store initial values for dirty checking
      setInitialContent(currentTemplate.content);
      setInitialName(currentTemplate.name);
      setInitialBusinessSig(currentTemplate.business_signature_required);
      setInitialCounterpartySig(currentTemplate.counterparty_signature_required);
      setIsDirty(false);

      // Re-enable dirty tracking after content is set
      // Use setTimeout to ensure all content updates are processed
      setTimeout(() => {
        isLoadingContent.current = false;
      }, 50);
    }
  }, [currentTemplate, editor]);

  // Handle contract template change with unsaved check
  const handleContractChange = useCallback((templateId: string) => {
    if (templateId === selectedTemplateId) return;

    if (hasUnsavedChanges()) {
      setPendingTemplateId(templateId);
      setShowUnsavedModal(true);
      return;
    }

    setSelectedTemplateId(templateId);
  }, [selectedTemplateId, hasUnsavedChanges]);

  // Insert variable at cursor position
  const handleInsertVariable = useCallback((variable: ContractVariable, categoryId: string) => {
    if (editor) {
      editor.chain().focus().insertContent({
        type: 'templateVariable',
        attrs: {
          variable: variable.id,
          label: variable.label,
          category: categoryId,
        },
      }).run();
    }
  }, [editor]);

  // Save template
  const handleSaveTemplate = useCallback(async () => {
    if (!currentTemplate || !editor || !currentTemplate.id) return;
    setSaving(true);

    const content = JSON.stringify(editor.getJSON());
    const response = await contractTemplateApi.update(currentTemplate.id, {
      content,
      name: contractName || currentTemplate.name,
      counterparty_signature_required: showCounterpartySignature,
      business_signature_required: showBusinessSignature,
    });

    if (response.data) {
      setTemplates(prev =>
        prev.map(t => t.id === currentTemplate.id ? response.data! : t)
      );
      // Reset dirty state after successful save
      setInitialContent(content);
      setInitialName(contractName || currentTemplate.name);
      setInitialBusinessSig(showBusinessSignature);
      setInitialCounterpartySig(showCounterpartySignature);
      setIsDirty(false);
    }
    setSaving(false);
  }, [currentTemplate, editor, contractName, showCounterpartySignature, showBusinessSignature]);

  // Handle exit with unsaved check
  const handleExit = useCallback(() => {
    if (hasUnsavedChanges()) {
      setPendingNavigationPath('/settings');
      setShowUnsavedModal(true);
    } else {
      nav('/settings');
    }
  }, [hasUnsavedChanges, nav]);

  // Discard changes and proceed with pending action
  const handleDiscardChanges = useCallback(() => {
    setShowUnsavedModal(false);
    setIsDirty(false);

    if (pendingNavigationPath) {
      nav(pendingNavigationPath);
      setPendingNavigationPath(null);
    } else if (pendingTemplateId) {
      // Use handleContractChange to handle both regular templates and fixture auto-copy
      handleContractChange(pendingTemplateId);
      setPendingTemplateId(null);
    }
  }, [pendingNavigationPath, pendingTemplateId, nav, handleContractChange]);

  // Create new template
  const handleCreateTemplate = useCallback(async () => {
    if (!currentBusiness || !newContractName.trim()) return;
    setSaving(true);

    const response = await contractTemplateApi.create({
      business: currentBusiness.id,
      document_type: newContractDocType,
      name: newContractName.trim(),
      content: JSON.stringify({ type: 'doc', content: [{ type: 'paragraph' }] }),
    });

    if (response.data) {
      setTemplates(prev => [...prev, response.data!]);
      setSelectedTemplateId(response.data.id);
      setShowCreateModal(false);
      setNewContractName('');
      setNewContractDocType('invoice');
    }
    setSaving(false);
  }, [currentBusiness, newContractName, newContractDocType]);

  // Delete template
  const handleDeleteTemplate = useCallback(async () => {
    if (!currentTemplate) return;
    setSaving(true);

    const response = await contractTemplateApi.delete(currentTemplate.id);
    if (!response.error) {
      setTemplates(prev => prev.filter(t => t.id !== currentTemplate.id));
      // Select another template
      const remaining = templates.filter(t => t.id !== currentTemplate.id);
      if (remaining.length > 0) {
        setSelectedTemplateId(remaining[0].id);
      } else {
        setSelectedTemplateId(null);
      }
      setShowDeleteModal(false);
    }
    setSaving(false);
  }, [currentTemplate, templates]);

  // Toggle template as default for its document type
  const handleToggleDefault = useCallback(async () => {
    if (!currentTemplate) return;
    setSaving(true);

    const response = await contractTemplateApi.setDefault(currentTemplate.id);
    if (response.data) {
      const newIsDefault = response.data.is_default;
      // Update all templates: if toggled on, unset others of same doc type
      setTemplates(prev =>
        prev.map(t => ({
          ...t,
          is_default: t.id === currentTemplate.id
            ? newIsDefault
            : newIsDefault && t.document_type === currentTemplate.document_type
              ? false
              : t.is_default,
        }))
      );
    }
    setSaving(false);
  }, [currentTemplate]);

  return (
    <div className="h-screen flex bg-background">
      {/* Left Sidebar */}
      <div className="w-56 border-r bg-muted/30 flex flex-col">
        {/* Header with Exit */}
        <div className="h-14 px-4 flex items-center border-b">
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-foreground gap-2"
            onClick={handleExit}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Exit</span>
          </Button>
        </div>

        {/* Create New Button */}
        <div className="p-3">
          <Button
            className="w-full gap-2"
            size="sm"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="h-4 w-4" />
            Create New Contract
          </Button>
        </div>

        <Separator />

        {/* Contract Templates */}
        <ScrollArea className="flex-1">
          <div className="p-2 space-y-1">
            <p className="px-3 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Templates
            </p>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : templates.length === 0 ? (
              <p className="px-3 py-4 text-sm text-muted-foreground text-center">
                No templates yet. Create one to get started.
              </p>
            ) : (
              templates.map((template) => {
                const isActive = selectedTemplateId === template.id;
                return (
                  <button
                    key={template.id}
                    onClick={() => handleContractChange(template.id)}
                    className={cn(
                      'w-full px-3 py-2 rounded-lg text-sm text-left transition-colors',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'hover:bg-muted text-foreground'
                    )}
                  >
                    <div className="font-medium">{template.name}</div>
                    <div className={cn(
                      'text-xs capitalize',
                      isActive ? 'text-primary/70' : 'text-muted-foreground'
                    )}>
                      {template.document_type.replace('_', ' ')}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Main Content - Editor */}
      <div className="flex-1 flex flex-col">
        {/* Top Bar */}
        <div className="h-14 px-6 flex items-center border-b">
          <div>
            <h1 className="font-semibold">{currentTemplate?.name || 'Select a template'}</h1>
            <p className="text-xs text-muted-foreground">
              {currentTemplate ? `Editing ${currentTemplate.document_type.replace('_', ' ')} template` : 'No template selected'}
            </p>
          </div>
        </div>

        {/* Editor */}
        <div className="flex-1 overflow-auto bg-white">
          <div className="max-w-4xl mx-auto">
            <EditorToolbar editor={editor} />
            <EditorContent editor={editor} />
            <SignatureBlock
              showBusiness={showBusinessSignature}
              showCounterparty={showCounterpartySignature}
              businessName={currentBusiness?.name || 'Your Business'}
              documentType={enabledDocType}
            />
          </div>
        </div>
      </div>

      {/* Right Sidebar - Variables */}
      <div className="w-72 border-l bg-muted/30 flex flex-col">
        <div className="h-14 px-4 flex items-center border-b">
          <h2 className="font-semibold text-sm">Contract Settings</h2>
        </div>
        <ScrollArea className="flex-1">
          {/* Contract Name */}
          <div className="px-4 py-4">
            <div className="relative group">
              <input
                type="text"
                value={contractName}
                onChange={(e) => {
                  setContractName(e.target.value);
                  setIsDirty(true);
                }}
                placeholder="Contract name..."
                className="w-full bg-transparent text-lg font-semibold border-none focus:outline-none pr-6 placeholder:text-muted-foreground/50"
              />
              <PenLine className="absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-muted-foreground/60 transition-colors pointer-events-none" />
            </div>
          </div>

          <Separator />

          {/* Document Type */}
          <div className="px-4 py-4 space-y-2">
            <h3 className="text-xs text-muted-foreground">Document Type</h3>
            <div className="flex items-center gap-2">
              {enabledDocType === 'invoice' && <Receipt className="h-4 w-4 text-primary" />}
              {enabledDocType === 'quote' && <FileQuestion className="h-4 w-4 text-primary" />}
              {enabledDocType === 'credit_memo' && <CreditCard className="h-4 w-4 text-primary" />}
              {enabledDocType === 'purchase_order' && <ShoppingCart className="h-4 w-4 text-primary" />}
              <span className="text-sm font-medium capitalize">
                {enabledDocType.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Default Template Toggle */}
          <div className="px-4 pb-4">
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label htmlFor="default-toggle" className="text-sm font-medium">
                  Default Template
                </Label>
                <p className="text-xs text-muted-foreground">
                  Auto-assign to new {enabledDocType.replace('_', ' ')}s
                </p>
              </div>
              <Switch
                id="default-toggle"
                checked={currentTemplate?.is_default ?? false}
                onCheckedChange={handleToggleDefault}
                disabled={saving}
              />
            </div>
          </div>

          <Separator />

          {/* Variables */}
          <div className="px-4 py-4 space-y-3">
            <h3 className="text-sm font-medium">Insert Variables</h3>
            <p className="text-xs text-muted-foreground">
              Click to insert at cursor. Variables are replaced when the contract is generated.
            </p>
          </div>
          <div className="px-2 pb-2 space-y-1">
            {variableCategories
              .filter((category) => {
                // Always show business and client
                if (category.id === 'business') return true;
                if (category.id === 'client') return true;
                // Show the category matching the selected document type
                return category.id === enabledDocType;
              })
              .map((category) => (
                <VariableCategorySection
                  key={category.id}
                  category={category}
                  onInsert={handleInsertVariable}
                />
              ))}
          </div>

          <Separator className="my-4" />

          {/* Signature Configuration */}
          <div className="px-4 pb-4 space-y-4">
            <div className="flex items-center gap-2">
              <PenLine className="h-4 w-4 text-muted-foreground" />
              <h3 className="text-sm font-medium">Signatures</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="business-sig" className="text-sm font-medium">
                    My Business
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Service/goods provider signature
                  </p>
                </div>
                <Switch
                  id="business-sig"
                  checked={showBusinessSignature}
                  onCheckedChange={(checked) => {
                    setShowBusinessSignature(checked);
                    setIsDirty(true);
                  }}
                />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="counterparty-sig" className="text-sm font-medium">
                    Counterparty
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {enabledDocType === 'purchase_order' ? 'Vendor' : 'Client'} signature
                  </p>
                </div>
                <Switch
                  id="counterparty-sig"
                  checked={showCounterpartySignature}
                  onCheckedChange={(checked) => {
                    setShowCounterpartySignature(checked);
                    setIsDirty(true);
                  }}
                />
              </div>
            </div>
          </div>

          <Separator className="my-4" />

          {/* Delete Template */}
          <div className="px-4 pb-4">
            <button
              onClick={() => setShowDeleteModal(true)}
              className="flex items-center gap-2 text-sm text-muted-foreground hover:text-destructive transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete Template</span>
            </button>
          </div>
        </ScrollArea>

        {/* Save Button - Fixed at bottom */}
        <div className="p-4 border-t">
          <Button
            className="w-full gap-2"
            onClick={handleSaveTemplate}
            disabled={!currentTemplate || saving || !isDirty}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Template
          </Button>
        </div>
      </div>

      {/* Create New Contract Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-contract-name" className="text-sm font-medium">
                Template Name
              </Label>
              <input
                id="new-contract-name"
                type="text"
                value={newContractName}
                onChange={(e) => setNewContractName(e.target.value)}
                placeholder="e.g., Standard Service Agreement"
                className="w-full px-3 py-2 text-sm border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>
            <div className="space-y-3">
              <Label className="text-sm font-medium">Document Type</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'invoice' as DocumentType, label: 'Invoice', icon: Receipt },
                  { id: 'quote' as DocumentType, label: 'Quote', icon: FileQuestion },
                  { id: 'credit_memo' as DocumentType, label: 'Credit Memo', icon: CreditCard },
                  { id: 'purchase_order' as DocumentType, label: 'Purchase Order', icon: ShoppingCart },
                ].map(({ id, label, icon: Icon }) => {
                  const isSelected = newContractDocType === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setNewContractDocType(id)}
                      className={cn(
                        'flex items-center gap-3 rounded-lg border p-3 text-sm transition-all text-left',
                        isSelected
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border text-muted-foreground hover:border-muted-foreground/50 hover:text-foreground'
                      )}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      <span className="font-medium">{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCreateModal(false);
                setNewContractName('');
                setNewContractDocType('invoice');
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateTemplate}
              disabled={!newContractName.trim() || saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Create Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Delete Contract Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete this contract template? This action cannot be undone.
            </p>
            <div className="text-sm text-muted-foreground bg-muted/50 p-4 rounded-lg border">
              <span className="font-medium text-foreground">Note:</span> Invoices that already have this contract attached will retain their contract. However, you will not be able to use this template for future invoices.
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteTemplate}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Delete Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Modal */}
      <Dialog open={showUnsavedModal} onOpenChange={setShowUnsavedModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved Changes</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              You have unsaved changes. Do you want to save before leaving?
            </p>
          </div>
          <DialogFooter className="flex gap-3">
            <Button
              variant="ghost"
              onClick={handleDiscardChanges}
            >
              Don't Save
            </Button>
            <Button
              onClick={async () => {
                await handleSaveTemplate();
                handleDiscardChanges();
              }}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
